import { useMemo } from "react";

import { type Currency } from "~/api/products/types";
import { type ReceiptReport, type SalesReport } from "~/hooks/queries/reports";

import {
  assembleReportSections,
  buildSalesSummary,
  fieldToCents,
  getFirstCurrency,
  sumCashRefunds
} from "./report-builders";
import { type CashDrawerData, type ReportSectionsData } from "./sections/types";

function buildCashDrawerDetails(
  report: SalesReport,
  receipt: ReceiptReport
): Omit<CashDrawerData, "currency"> {
  const cashSalesCents = fieldToCents(receipt.sections.paymentsMethods.cash);
  const cashRefundsCents = sumCashRefunds(receipt);

  // Sum balances across every currency present, instead of guessing the
  // currency from the first sales entry (which would miss a balance whose
  // currency differs from the first sales currency).
  const sumBalance = (
    balance: SalesReport["tillSession"]["openCashBalance"] | undefined
  ) =>
    Object.values(balance ?? {}).reduce((sum: number, v) => sum + (v ?? 0), 0);

  const openingFloat = sumBalance(report.tillSession.openCashBalance);
  const actualCash = sumBalance(report.tillSession.closeCashBalance);
  const expectedCash = sumBalance(report.tillSession.expectedCashBalance);

  const difference = actualCash - expectedCash;

  return {
    openingFloat,
    cashSales: cashSalesCents,
    cashRefunds: cashRefundsCents,
    expectedCash,
    actualCash,
    overShort: difference === 0 ? "balanced" : difference,
    manualOpenings: report.tillSession.manualCashDrawerOpenings ?? 0,
    // Backward compatibility: old Z-reports only have `cashDrawerOpenings` (single total).
    // If the new split fields are both 0, fall back to the legacy field.
    automaticOpenings:
      (report.tillSession.manualCashDrawerOpenings ?? 0) > 0 ||
      (report.tillSession.automaticCashDrawerOpenings ?? 0) > 0
        ? report.tillSession.automaticCashDrawerOpenings ?? 0
        : report.tillSession.cashDrawerOpenings ?? 0
  };
}

export function useReportSections(
  report: SalesReport,
  receipt: ReceiptReport,
  fallbackCurrency?: Currency
): ReportSectionsData {
  return useMemo(() => {
    const currency = getFirstCurrency(receipt, fallbackCurrency);

    return assembleReportSections({
      receipt,
      salesSummary: buildSalesSummary(receipt, currency),
      cashDrawer: buildCashDrawerDetails(report, receipt),
      currency
    });
  }, [report, receipt, fallbackCurrency]);
}
